"use client";

import cx from "classnames";
import { useState, useMemo, useCallback } from "react";
import {
  SPORTSBOOK_BETSLIP_CTA_CLICKED,
  SPORTSBOOK_ODDS_SELECTED,
  SPORTSBOOK_READY_TO_BET_MODAL_CLOSED,
} from "@/constants/amplitude";
import { Maybe } from "@/graphql/gql/graphql";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import Lock from "../svg/Lock";
import Styles from "./OddsBtn.module.scss";

type OddsBtnProps = {
  classButton?: string;
  classHandicap?: string;
  handicap?: Maybe<number | string>;
  isSuspended?: boolean;
  onClick: () => void;
  odds: number | string;
};

/**
 * All uses of OddsBtn that should link to Sportsbook must use this Hook to control
 * a <RedirectToSportbook> modal. This ensures users must confirm their intent before
 * navigating away.
 */
export const useRedirectConfirmationModal = (
  sport: string, // "NFL", "NBA", etc
  eventName: string, // "dallas-mavericks-@-philadelphia-76ers-33998277"
  ampModule?: string
) => {
  const [url, setUrl] = useState("");
  const [showModal, setShowModal] = useState(false);
  const { logAmplitudeEvent } = useAmplitude();
  const ampProps = useMemo(
    () => ({
      Module: ampModule ?? "coupon",
      Sport: sport ?? "",
      "Event Name": eventName ?? "",
    }),
    [sport, eventName, ampModule]
  );

  const onOddsClick = useCallback(
    (redirectUrl: string) => {
      setUrl(redirectUrl);
      setShowModal(true);
      logAmplitudeEvent(SPORTSBOOK_ODDS_SELECTED, { ...ampProps });
    },
    [ampProps, logAmplitudeEvent]
  );

  const onClickPrimaryCta = () => {
    logAmplitudeEvent(SPORTSBOOK_BETSLIP_CTA_CLICKED, {
      ...ampProps,
      "Link URL": url,
      "Link Text": "go_to_betslip",
      Module: "ready_to_bet_modal",
    });
  };

  const onRequestClose = () => {
    setShowModal(false);
    logAmplitudeEvent(SPORTSBOOK_READY_TO_BET_MODAL_CLOSED, {
      ...ampProps,
      Module: "ready_to_bet_modal",
    });
  };

  const redirectToSportsbookProps = {
    sportsbookRedirectUrl: url,
    isOpen: showModal,
    onClickPrimaryCta,
    onRequestClose,
  };

  return { onOddsClick, redirectToSportsbookProps };
};

const OddsBtn = ({
  handicap,
  isSuspended,
  onClick,
  odds,
  classButton,
  classHandicap,
}: OddsBtnProps) => (
  <button
    type="button"
    className={cx(Styles.button, classButton)}
    onClick={onClick}
    disabled={isSuspended}
  >
    {isSuspended ? (
      <Lock />
    ) : (
      <>
        {handicap && handicap !== "0" ? (
          <div className={cx(Styles.handicap, classHandicap)}>{handicap}</div>
        ) : null}
        <div className={Styles.odds}>{odds}</div>
      </>
    )}
  </button>
);

export default OddsBtn;
